/**
 * A customer's App Store Connect delivery key.
 *
 * Separate from the signing certificate, and it has to be: the certificate signs the app, this
 * hands it to Apple, and a customer can revoke either without the other. Three things arrive
 * together — an issuer id, a key id, and the .p8 Apple offers exactly once.
 *
 * The key is tried against Apple before it is stored, which also answers whether their account
 * has an app with this bundle identifier yet. That is the last thing a delivery discovers and
 * the most annoying one to discover late.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError, api, type AppStoreKey as Stored, type Client } from '../api';

export function AppStoreKey({ client, isAdmin, onToast, onChanged }: {
  client: Client;
  isAdmin: boolean;
  onToast: (message: string) => void;
  onChanged: () => void;
}) {
  const [stored, setStored] = useState<Stored | null>(null);
  const [replacing, setReplacing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [note, setNote] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const found = await api.appStoreKey(client.slug);
      setStored(found.present === false ? null : found);
    } catch {
      setStored(null);
    }
  }, [client.slug]);

  useEffect(() => { void reload(); }, [reload]);

  const form = (
    <form
      className="form"
      onSubmit={(event) => {
        event.preventDefault();
        setErrors([]);
        setNote(null);
        setBusy(true);
        api.uploadAppStoreKey(client.slug, new FormData(event.currentTarget))
          .then((result) => {
            onToast(`Delivering with key ${result.keyId}`);
            setNote(result.note ?? null);
            setReplacing(false);
            void reload();
            onChanged();
          })
          .catch((failure: ApiError) => setErrors(failure.errors?.length ? failure.errors : [failure.message]))
          .finally(() => setBusy(false));
      }}
    >
      {errors.length > 0 && (
        <div className="errors">{errors.map((message) => <span key={message}>{message}</span>)}</div>
      )}
      <div className="field">
        <label htmlFor={`p8-${client.slug}`}>Private key</label>
        <input id={`p8-${client.slug}`} name="p8" type="file" accept=".p8" required />
        <span className="hint">
          The <span className="mono">AuthKey_….p8</span> file. Apple offers it once, when the key is
          created; it cannot be downloaded again.
        </span>
      </div>
      <div className="field">
        <label htmlFor={`issuer-${client.slug}`}>Issuer ID</label>
        <input id={`issuer-${client.slug}`} name="issuerId" type="text" required
               placeholder="6b4d1f2a-0000-1111-2222-3c4d5e6f7a8b" />
        <span className="hint">
          Above the key list in App Store Connect, under Users and Access &rarr; Integrations. The
          same for every key in their account.
        </span>
      </div>
      <div className="field">
        <label htmlFor={`keyid-${client.slug}`}>Key ID (optional)</label>
        <input id={`keyid-${client.slug}`} name="keyId" type="text" placeholder="taken from the file name" />
      </div>
      <div className="actions">
        <button className="btn" type="submit" disabled={busy}>
          {busy ? 'Asking Apple' : stored ? 'Replace' : 'Store it'}
        </button>
        {stored && (
          <button type="button" className="btn quiet" onClick={() => setReplacing(false)}>Cancel</button>
        )}
      </div>
    </form>
  );

  if (!stored) {
    return (
      <section className="section">
        <div className="section-head">
          <h2>App Store delivery</h2>
          <span className="note">Without it, builds hand back an .ipa to upload by hand</span>
        </div>
        {form}
      </section>
    );
  }

  return (
    <section className="section">
      <div className="section-head">
        <h2>App Store delivery</h2>
        <span className="note">{stored.name ?? `key ${stored.keyId}`}</span>
      </div>

      <dl className="facts">
        <div className="fact">
          <dt>Key</dt>
          <dd className="mono">{stored.keyId}</dd>
        </div>
        <div className="fact prose">
          <dt>Issuer</dt>
          <dd className="mono">{stored.issuerId}</dd>
        </div>
        <div className="fact prose">
          <dt>Added</dt>
          <dd>{new Date(stored.uploadedAt).toLocaleDateString()} by {stored.uploadedBy}</dd>
        </div>
      </dl>

      {note && (
        <div className="warnings">
          <h3>Worth a look</h3>
          <p className="warning">{note}</p>
        </div>
      )}

      {isAdmin && !replacing && (
        <div className="actions" style={{ marginTop: 16 }}>
          <button type="button" className="btn quiet" onClick={() => setReplacing(true)}>
            Replace
          </button>
        </div>
      )}
      {replacing && <div style={{ marginTop: 18 }}>{form}</div>}
    </section>
  );
}
